import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDebounce from '../../hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { leadsAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import Pagination from '../../components/Pagination';
import TableSkeleton from '../../components/TableSkeleton';
import {
  Search, Filter, X, Eye, Edit, Trash2, Plus,
  Mail, Phone, AlertCircle, RefreshCw, Sparkles, Brain
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminLeads.css';

const AdminLeads = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  // Filters derived from URL params
  const filters = {
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    urgency: searchParams.get('urgency') || '',
    source: searchParams.get('source') || '',
  };

  // Page derived from URL params
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const setPage = (p) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', p);
    setSearchParams(newParams, { replace: true });
  };

  // Update a single filter key in the URL (resets page to 1)
  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.delete('page');
    setSearchParams(newParams, { replace: true });
  };

  // Local search state — debounced before writing to URL
  const [localSearch, setLocalSearch] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(localSearch, 300);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      newParams.set('search', debouncedSearch);
    } else {
      newParams.delete('search');
    }
    newParams.delete('page');
    setSearchParams(newParams, { replace: true });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadLeads();
  }, [filters.status, filters.urgency, filters.source, debouncedSearch, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape key — close Lead Detail modal
  useEffect(() => {
    if (!selectedLead) return;
    const handler = (e) => { if (e.key === 'Escape') setSelectedLead(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [selectedLead]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const res = await leadsAPI.getAll({ ...filters, search: debouncedSearch, page: currentPage, limit: 25 });
      setLeads(res.data.leads);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
      }
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await leadsAPI.update(leadId, { status: newStatus });
      loadLeads();
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm(t('admin.leads.confirmDelete'))) return;
    try {
      await leadsAPI.delete(leadId);
      loadLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: '#3b82f6',
      contacted: '#8b5cf6',
      quoted: '#f59e0b',
      converted: '#10b981',
      closed: '#6b7280',
      lost: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      urgent: '#ef4444',
      high: '#f59e0b',
      normal: '#3b82f6',
      low: '#6b7280'
    };
    return colors[urgency] || '#6b7280';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      
      <div className="admin-content">
        <header className="content-header">
          <div>
            <h1>{t('admin.leads.title')}</h1>
            <p>{t('admin.leads.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button onClick={loadLeads} className="admin-btn admin-btn-secondary" aria-label={t('admin.common.refresh')}>
              <RefreshCw size={18} />
            </button>
            <button className="admin-btn admin-btn-primary">
              <Plus size={18} />
              {t('admin.leads.new')}
            </button>
          </div>
        </header>

        {/* Filters */}
        <div className="admin-filters">
          <div className="admin-search">
            <Search size={20} />
            <input
              type="text"
              placeholder={t('admin.leads.search')}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
            className="admin-select"
          >
            <option value="">{t('admin.leads.allStatuses')}</option>
            <option value="new">{t('admin.leads.statusNew')}</option>
            <option value="contacted">{t('admin.leads.statusContacted')}</option>
            <option value="quoted">{t('admin.leads.statusQuoted')}</option>
            <option value="converted">{t('admin.leads.statusConverted')}</option>
            <option value="closed">{t('admin.leads.statusClosed')}</option>
            <option value="lost">{t('admin.leads.statusLost')}</option>
          </select>

          <select
            value={filters.urgency}
            onChange={(e) => updateFilter('urgency', e.target.value)}
            className="admin-select"
          >
            <option value="">{t('admin.leads.allUrgencies')}</option>
            <option value="urgent">{t('admin.leads.urgencyUrgent')}</option>
            <option value="high">{t('admin.leads.urgencyHigh')}</option>
            <option value="normal">{t('admin.leads.urgencyNormal')}</option>
            <option value="low">{t('admin.leads.urgencyLow')}</option>
          </select>

          {(filters.status || filters.urgency || filters.search) && (
            <button
              onClick={() => {
                setLocalSearch('');
                setSearchParams(new URLSearchParams(), { replace: true });
              }}
              className="admin-btn admin-btn-secondary"
            >
              <X size={18} />
              Reset
            </button>
          )}
        </div>

        {/* Leads Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.leads.colName')}</th>
                <th>{t('admin.leads.colContact')}</th>
                <th>{t('admin.leads.colCompany')}</th>
                <th>{t('admin.leads.colUrgency')}</th>
                <th>{t('admin.leads.colStatus')}</th>
                <th>{t('admin.leads.colDate')}</th>
                <th>{t('admin.leads.colActions')}</th>
              </tr>
            </thead>
            {loading ? (
              <TableSkeleton rows={8} columns={6} />
            ) : null}
            <tbody>
              {!loading && leads.map((lead) => (
                <tr key={lead._id}>
                  <td>
                    <strong>{lead.name}</strong>
                  </td>
                  <td>
                    <div className="lead-contact-cell">
                      <span className="lead-contact-line">
                        <Mail size={14} /> {lead.email}
                      </span>
                      {lead.phone && (
                        <span className="lead-contact-line">
                          <Phone size={14} /> {lead.phone}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{lead.company || '-'}</td>
                  <td>
                    <span
                      className="lead-badge"
                      style={{ background: `${getUrgencyColor(lead.urgency)}20`, color: getUrgencyColor(lead.urgency) }}
                    >
                      {lead.urgency?.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <select
                      value={lead.status}
                      onChange={(e) => updateLeadStatus(lead._id, e.target.value)}
                      className="admin-select-inline"
                      style={{ color: getStatusColor(lead.status) }}
                    >
                      <option value="new">{t('admin.leads.statusNew')}</option>
                      <option value="contacted">{t('admin.leads.statusContacted')}</option>
                      <option value="quoted">{t('admin.leads.statusQuoted')}</option>
                      <option value="converted">{t('admin.leads.statusConverted')}</option>
                      <option value="closed">{t('admin.leads.statusClosed')}</option>
                      <option value="lost">{t('admin.leads.statusLost')}</option>
                    </select>
                  </td>
                  <td style={{ fontSize: '13px', color: '#a1a1aa' }}>
                    {formatDate(lead.createdAt)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setSelectedLead(lead)}
                        className="action-btn action-btn-view"
                        title={t('admin.leads.titleView')}
                        aria-label={t('admin.leads.titleView')}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => deleteLead(lead._id)}
                        className="action-btn action-btn-delete"
                        title={t('admin.leads.titleDelete')}
                        aria-label={t('admin.leads.titleDelete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {leads.length === 0 && !loading && (
          <div className="admin-empty">
            <AlertCircle size={48} />
            <p>{t('admin.leads.noLeads')}</p>
          </div>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => setPage(newPage)}
          />
        )}

        {/* Lead Detail Modal */}
        {selectedLead && (
          <div className="admin-modal-overlay" onClick={() => setSelectedLead(null)}>
            <div className="admin-modal" role="dialog" aria-modal="true" aria-labelledby="lead-detail-modal-title" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 id="lead-detail-modal-title">{t('admin.leads.detailTitle')}</h2>
                <button onClick={() => setSelectedLead(null)} className="modal-close" aria-label={t('admin.common.close')}>
                  <X size={24} />
                </button>
              </div>
              <div className="modal-body">
                <div className="lead-detail-grid">
                  <div className="lead-detail-item">
                    <strong>{t('admin.leads.fieldName')}</strong>
                    <span>{selectedLead.name}</span>
                  </div>
                  <div className="lead-detail-item">
                    <strong>{t('admin.leads.fieldEmail')}</strong>
                    <span>{selectedLead.email}</span>
                  </div>
                  <div className="lead-detail-item">
                    <strong>{t('admin.leads.fieldPhone')}</strong>
                    <span>{selectedLead.phone || 'N/A'}</span>
                  </div>
                  <div className="lead-detail-item">
                    <strong>{t('admin.leads.fieldCompany')}</strong>
                    <span>{selectedLead.company || 'N/A'}</span>
                  </div>
                  <div className="lead-detail-item">
                    <strong>{t('admin.leads.fieldUrgency')}</strong>
                    <span
                      style={{ color: getUrgencyColor(selectedLead.urgency) }}
                    >
                      {selectedLead.urgency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="lead-detail-item">
                    <strong>{t('admin.leads.fieldStatus')}</strong>
                    <span
                      style={{ color: getStatusColor(selectedLead.status) }}
                    >
                      {selectedLead.status}
                    </span>
                  </div>
                  <div className="lead-detail-item">
                    <strong>{t('admin.leads.fieldDate')}</strong>
                    <span>{formatDate(selectedLead.createdAt)}</span>
                  </div>
                </div>

                <div className="lead-message-full">
                  <strong>{t('admin.leads.fieldMessage')}</strong>
                  <p>{selectedLead.message}</p>
                </div>

                {/* AI Analysis Section */}
                {selectedLead.aiAnalysis && (
                  <div className="ai-analysis-section">
                    <h3><Brain size={20} /> {t('admin.leads.aiAnalysis')}</h3>
                    <div className="ai-insights">
                      <div className="ai-badge">
                        <span className="ai-label">{t('admin.leads.aiPartType')}</span>
                        <span className="ai-value">{selectedLead.aiAnalysis.partType || 'N/A'}</span>
                      </div>
                      <div className="ai-badge">
                        <span className="ai-label">{t('admin.leads.aiComplexity')}</span>
                        <span className="ai-value">{selectedLead.aiAnalysis.complexity || 'N/A'}</span>
                      </div>
                      <div className="ai-badge">
                        <span className="ai-label">{t('admin.leads.aiMaterial')}</span>
                        <span className="ai-value">{selectedLead.aiAnalysis.suggestedMaterial || 'N/A'}</span>
                      </div>
                      <div className="ai-badge">
                        <span className="ai-label">{t('admin.leads.aiLeadTime')}</span>
                        <span className="ai-value">{selectedLead.aiAnalysis.estimatedLeadTime || 'N/A'}</span>
                      </div>
                      <div className="ai-badge">
                        <span className="ai-label">{t('admin.leads.aiAction')}</span>
                        <span className="ai-value">{selectedLead.aiAnalysis.recommendedAction || 'N/A'}</span>
                      </div>
                      <div className="ai-badge">
                        <span className="ai-label">{t('admin.leads.aiConfidence')}</span>
                        <span className="ai-value">{selectedLead.aiAnalysis.confidenceScore || 0}%</span>
                      </div>
                    </div>
                    {selectedLead.aiAnalysis.summary && (
                      <div className="ai-summary">
                        <strong>{t('admin.leads.aiSummary')}</strong>
                        <p>{selectedLead.aiAnalysis.summary}</p>
                      </div>
                    )}
                    {selectedLead.aiAnalysis.keyRequirements && selectedLead.aiAnalysis.keyRequirements.length > 0 && (
                      <div className="ai-requirements">
                        <strong>{t('admin.leads.aiRequirements')}</strong>
                        <ul>
                          {selectedLead.aiAnalysis.keyRequirements.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLeads;
